import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Bank, Plus, Trash, ToggleLeft, ToggleRight, Wallet, CurrencyDollar, QrCode, Globe, UploadSimple, Sparkle, X, Money, Check, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TYPE_ICONS = {
  bank: Bank, upi: Wallet, paypal: CurrencyDollar, stripe_connect: CurrencyDollar,
  razorpay: CurrencyDollar, venmo: CurrencyDollar, mpesa: Wallet, qr: QrCode, crypto: Globe,
  cash: Money,
};
const TYPE_HINTS = {
  bank:           { account_holder: 'Account holder name', account_number: 'Account number / IBAN', routing_swift: 'Routing / SWIFT / IFSC', bank_name: 'Bank name', country: 'Country' },
  upi:            { upi_id: 'e.g. yourname@hdfc' },
  paypal:         { paypal_email: 'PayPal email (e.g. you@gmail.com)', paypal_link: 'paypal.me link (e.g. paypal.me/yourname)' },
  stripe_connect: { stripe_link: 'Stripe Connect link / account ID' },
  razorpay:       { razorpay_link: 'Razorpay link or merchant ID' },
  venmo:          { username: 'Venmo username (without @)' },
  mpesa:          { phone: 'M-Pesa phone number' },
  qr:             { qr_image_url: 'QR image URL (use upload or generate above)', qr_caption: 'Caption shown under the QR (optional)' },
  crypto:         { currency: 'BTC / USDT / ETH / ...', wallet_address: 'Wallet address' },
  cash:           { instructions: 'e.g. "Pay on arrival at the venue reception"' },
};

const SellerPaymentMethodsCard = () => {
  const [types, setTypes] = useState([]);
  const [enabledGlobally, setEnabledGlobally] = useState(true);
  const [methods, setMethods] = useState([]);
  const [accountEnabled, setAccountEnabled] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ type: 'bank', label: '', details: {}, is_active: true });
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null); // null | { message, retriable }
  const [qrPayload, setQrPayload] = useState('');
  const [qrBusy, setQrBusy] = useState(false);
  const qrFileRef = useRef(null);
  // Quick-activate (multi-select checkbox panel) state
  const [quickPick, setQuickPick] = useState(() => new Set());
  const [quickBusy, setQuickBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoadError(null);
    try {
      const [t, mine] = await Promise.all([
        axios.get(`${API_URL}/api/direct-payments/types`),
        axios.get(`${API_URL}/api/seller/payment-methods`),
      ]);
      setEnabledGlobally(!!t.data?.feature_enabled);
      setTypes(t.data?.types || []);
      setAccountEnabled(!!mine.data?.feature_enabled);
      setMethods(mine.data?.methods || []);
    } catch (e) {
      const httpStatus = e.response?.status;
      if (httpStatus === 401 || httpStatus === 403) {
        // User isn't a seller (or session expired) — silently hide the card.
        setLoadError({ message: null, retriable: false });
      } else {
        // Real network / server error — surface with retry.
        const detail = e.response?.data?.detail || e.message || 'Could not load payment methods';
        setLoadError({ message: detail, retriable: true });
        toast.error(`Payment methods: ${detail}`);
      }
    } finally { setLoaded(true); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!form.type) return;
    setForm(f => ({ ...f })); // no-op for lint
    try {
      await axios.post(`${API_URL}/api/seller/payment-methods`, form);
      setShowNew(false);
      setForm({ type: types[0]?.id || 'bank', label: '', details: {}, is_active: true });
      fetchAll();
      toast.success('Payment method added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggleActive = async (m) => {
    try { await axios.put(`${API_URL}/api/seller/payment-methods/${m.method_id}`, { is_active: !m.is_active }); fetchAll(); }
    catch (e) { toast.error('Toggle failed'); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove "${m.label}"?`)) return;
    try { await axios.delete(`${API_URL}/api/seller/payment-methods/${m.method_id}`); fetchAll(); toast.success('Removed'); }
    catch (e) { toast.error('Delete failed'); }
  };

  // ---- QR helpers (upload existing QR image OR generate one from a payment link) ----
  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Image too large. Max 3MB'); return; }
    setQrBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/seller/payment-methods/upload-qr`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, details: { ...f.details, qr_image_url: data.qr_image_url } }));
      toast.success('QR uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setQrBusy(false);
      if (qrFileRef.current) qrFileRef.current.value = '';
    }
  };

  const handleQrGenerate = async () => {
    const payload = qrPayload.trim();
    if (!payload) { toast.error('Enter a payment link / UPI ID / address to encode'); return; }
    setQrBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/seller/payment-methods/generate-qr`, { payload });
      setForm(f => ({ ...f, details: { ...f.details, qr_image_url: data.qr_image_url } }));
      toast.success('QR generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setQrBusy(false);
    }
  };

  const clearQr = () => setForm(f => ({ ...f, details: { ...f.details, qr_image_url: '' } }));

  // ---- Quick-Activate (multi-select checkbox panel) ----
  const toggleQuickPick = (typeId) => {
    setQuickPick(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId); else next.add(typeId);
      return next;
    });
  };
  const activeTypes = new Set(methods.filter(m => m.is_active).map(m => m.type));
  const handleBulkActivate = async () => {
    const picks = Array.from(quickPick);
    if (picks.length === 0) { toast.error('Tick at least one method to activate'); return; }
    setQuickBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/seller/payment-methods/bulk-activate`, { types: picks });
      const newCount = (data.created || []).length;
      const reactCount = (data.reactivated || []).length;
      toast.success(`${newCount + reactCount} method${newCount + reactCount === 1 ? '' : 's'} active${newCount && reactCount ? ` (${newCount} new, ${reactCount} re-enabled)` : ''}`);
      setQuickPick(new Set());
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk activate failed');
    } finally { setQuickBusy(false); }
  };

  if (!loaded) return null;
  // Silent hide when user is not a seller (401/403) OR admin disabled platform-wide
  if (loadError && !loadError.retriable) return null;
  if (!loadError && !enabledGlobally) return null;

  if (loadError && loadError.retriable) {
    return (
      <div className="bg-white rounded-2xl border border-rose-200 p-5" data-testid="seller-payment-methods-card-error">
        <h3 className="text-base font-bold text-slate-900">Direct Payment Methods</h3>
        <p className="text-sm text-rose-600 mt-2">{loadError.message}</p>
        <Button size="sm" onClick={fetchAll} className="mt-3 bg-slate-900 hover:bg-slate-800 h-9 rounded-lg text-xs" data-testid="seller-payment-methods-retry-btn">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="seller-payment-methods-card">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
        <div>
          <h3 className="text-base font-bold text-slate-900">Direct Payment Methods</h3>
          <p className="text-xs text-slate-500 mt-0.5">Receive direct payments from buyers — UPI, PayPal, Bank, QR, Crypto and more. Buyers see your active methods as alternatives to card checkout.</p>
        </div>
        {accountEnabled && (
          <Button size="sm" onClick={() => { setForm({ type: types[0]?.id || 'bank', label: '', details: {}, is_active: true }); setShowNew(s => !s); }}
            className="bg-emerald-500 hover:bg-emerald-600 h-9 rounded-lg text-xs" data-testid="add-payment-method-btn">
            <Plus size={14} className="mr-1" /> Add Method
          </Button>
        )}
      </div>

      {!accountEnabled && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700" data-testid="dpm-disabled-banner">
          Direct payments are currently disabled for your account by admin. Reach out for activation.
        </div>
      )}

      {showNew && accountEnabled && (
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3" data-testid="new-payment-method-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Type</label>
              <Select value={form.type} onValueChange={(v) => { setForm({ ...form, type: v, details: {} }); setQrPayload(''); }}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="method-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Label (optional)</label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. My HDFC, Primary UPI" className="h-9 text-sm mt-1" data-testid="method-label-input" />
            </div>
          </div>

          {form.type === 'qr' && (
            <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/60 p-3 space-y-2.5" data-testid="qr-panel">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700">
                <QrCode size={14} weight="fill" /> QR Code — upload an image or auto-generate one
              </div>
              <div className="flex flex-wrap items-stretch gap-2">
                {/* Upload */}
                <input ref={qrFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={handleQrUpload} data-testid="qr-file-input" />
                <Button size="sm" variant="outline" disabled={qrBusy}
                  onClick={() => qrFileRef.current?.click()}
                  className="h-9 rounded-lg text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  data-testid="qr-upload-btn">
                  <UploadSimple size={14} className="mr-1" /> Upload QR image
                </Button>
                {/* Generate */}
                <div className="flex-1 min-w-[180px] flex items-center gap-2">
                  <Input value={qrPayload} onChange={(e) => setQrPayload(e.target.value)}
                    placeholder="upi://pay?pa=you@hdfc  /  paypal.me/you  /  wallet address"
                    className="h-9 text-xs flex-1" data-testid="qr-payload-input" />
                  <Button size="sm" disabled={qrBusy || !qrPayload.trim()} onClick={handleQrGenerate}
                    className="h-9 rounded-lg text-xs bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 hover:from-indigo-600 hover:to-fuchsia-600 text-white"
                    data-testid="qr-generate-btn">
                    <Sparkle size={14} className="mr-1" weight="fill" /> Generate
                  </Button>
                </div>
              </div>
              {/* Preview */}
              {form.details.qr_image_url ? (
                <div className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 p-2" data-testid="qr-preview">
                  <img src={`${API_URL}${form.details.qr_image_url}`} alt="QR preview" className="w-16 h-16 object-contain rounded bg-slate-50" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-slate-500 truncate">Saved at <span className="font-mono">{form.details.qr_image_url}</span></p>
                    <p className="text-[11px] text-emerald-600 font-medium mt-0.5">Ready — fill the optional caption below if needed, then Save.</p>
                  </div>
                  <button onClick={clearQr} className="text-slate-400 hover:text-red-500 p-1" title="Remove QR" data-testid="qr-clear-btn">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-slate-500">No QR yet. Upload an existing one (PNG/JPG, ≤3MB) or paste a payment link/UPI ID above and click <strong>Generate</strong>.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {(types.find(t => t.id === form.type)?.fields || []).map(f => (
              <Input key={f}
                placeholder={TYPE_HINTS[form.type]?.[f] || f}
                value={form.details[f] || ''}
                onChange={(e) => setForm({ ...form, details: { ...form.details, [f]: e.target.value } })}
                className="h-9 text-sm"
                disabled={form.type === 'qr' && f === 'qr_image_url'}
                data-testid={`method-field-${f}`} />
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setShowNew(false)} className="text-slate-600 h-9 rounded-lg text-xs">Cancel</Button>
            <Button size="sm" onClick={submit} className="bg-emerald-500 hover:bg-emerald-600 text-white h-9 rounded-lg text-xs" data-testid="save-payment-method-btn">Save</Button>
          </div>
        </div>
      )}

      {/* ---- Quick Activate: multi-select checkboxes (incl. Cash) ---- */}
      {accountEnabled && types.length > 0 && (
        <div className="mt-4 bg-gradient-to-br from-emerald-50/70 via-white to-teal-50/70 border border-emerald-200/70 rounded-xl p-3.5" data-testid="quick-activate-panel">
          <div className="flex items-center gap-1.5 mb-1">
            <Lightning size={14} weight="fill" className="text-emerald-600" />
            <h4 className="text-sm font-bold text-slate-900">Quick Activate</h4>
            <span className="text-[10px] text-slate-500 ml-1">Tick the methods you accept, then click <strong>Activate Selected</strong>.</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
            {types.map(t => {
              const Icon = TYPE_ICONS[t.id] || Wallet;
              const picked = quickPick.has(t.id);
              const alreadyActive = activeTypes.has(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleQuickPick(t.id)}
                  className={`relative flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all ${
                    picked
                      ? 'border-emerald-500 bg-emerald-100 ring-2 ring-emerald-300/50 shadow-sm'
                      : alreadyActive
                        ? 'border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`quick-pick-${t.id}`}
                >
                  <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${picked ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300'}`}>
                    {picked && <Check size={12} weight="bold" className="text-white" />}
                  </span>
                  {t.icon_url ? (
                    <img src={`${API_URL}${t.icon_url}`} alt="" className="w-4 h-4 object-contain shrink-0" />
                  ) : (
                    <Icon size={14} className="text-indigo-500 shrink-0" weight="fill" />
                  )}
                  <span className="text-xs font-semibold text-slate-800 truncate flex-1">{t.label}</span>
                  {alreadyActive && (
                    <span className="text-[9px] uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded-full" data-testid={`quick-active-badge-${t.id}`}>ON</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 mt-3">
            <p className="text-[11px] text-slate-500">
              {quickPick.size > 0
                ? <>Selected: <strong className="text-emerald-700">{quickPick.size}</strong>. Methods will be created with empty details — edit them below to fill account info.</>
                : 'Cash activates instantly with no setup. Other methods will be created — edit them below to add account details.'}
            </p>
            <Button size="sm" onClick={handleBulkActivate} disabled={quickBusy || quickPick.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 rounded-lg text-xs shrink-0"
              data-testid="bulk-activate-btn">
              <Lightning size={14} className="mr-1" weight="fill" /> Activate Selected{quickPick.size > 0 ? ` (${quickPick.size})` : ''}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {methods.length === 0 && accountEnabled && !showNew && (
          <div className="text-center text-slate-500 text-sm py-6">No payment methods yet. Click <strong>Add Method</strong> to receive direct payments.</div>
        )}
        {methods.map(m => {
          const Icon = TYPE_ICONS[m.type] || Wallet;
          const sub = Object.entries(m.details || {})
            .filter(([k, v]) => v && k !== 'qr_image_url')
            .map(([, v]) => v).slice(0, 2).join(' · ');
          const qrUrl = m.type === 'qr' ? m.details?.qr_image_url : null;
          return (
            <div key={m.method_id} data-testid={`payment-method-${m.method_id}`}
              className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
              {qrUrl ? (
                <img src={`${API_URL}${qrUrl}`} alt="QR" className="w-10 h-10 object-contain rounded bg-slate-50 border border-slate-200 shrink-0" data-testid={`qr-thumb-${m.method_id}`} />
              ) : (
                <Icon size={20} className="text-indigo-500" weight="fill" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{m.label}</div>
                <div className="text-[11px] text-slate-500 truncate">{sub || (qrUrl ? 'QR code attached' : '—')}</div>
              </div>
              <button onClick={() => toggleActive(m)} className="text-slate-500 hover:text-slate-900 p-1.5" data-testid={`toggle-payment-method-${m.method_id}`} title={m.is_active ? 'Active — click to disable' : 'Disabled — click to enable'}>
                {m.is_active ? <ToggleRight size={20} weight="fill" className="text-emerald-500" /> : <ToggleLeft size={20} />}
              </button>
              <button onClick={() => remove(m)} className="text-red-400 hover:text-red-600 p-1.5" data-testid={`delete-payment-method-${m.method_id}`}>
                <Trash size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SellerPaymentMethodsCard;
